import { useEffect, useState } from "react";
import type { Rpc } from "../lib/rpc";

interface PlatformPromoCfg {
  TIMES: string[];
  SOLO_PERCENTAGE: number;
  VIDEO_PERCENTAGE: number;
  MEDIA_TYPES: string[];
  paused?: boolean;
}
type PromoCfg = Record<string, PlatformPromoCfg>;

interface Account {
  id: string;
  displayName: string;
  username: string;
  persona?: string;
}

// Preferred ordering — anything in the config but not listed here falls in
// alphabetically at the end. New platforms (e.g. ONLYFANS once #3 lands)
// auto-appear without needing a code change.
const PREFERRED_ORDER = ["TWITTER", "FETLIFE", "REDDIT", "REDGIFS", "ONLYFANS", "FANSLY", "MANYVIDS"];
const PLATFORM_ICON: Record<string, string> = {
  TWITTER: "𝕏", FETLIFE: "FL", REDDIT: "R", REDGIFS: "RG",
  ONLYFANS: "OF", FANSLY: "Fy", MANYVIDS: "MV", GMAIL: "✉",
};
// Pretty-print platform names — promo_config.json uses UPPERCASE keys but
// the card header should show the human form (RedGifs, OnlyFans, etc.).
const PLATFORM_NAME: Record<string, string> = {
  TWITTER: "Twitter / X", FETLIFE: "FetLife", REDDIT: "Reddit", REDGIFS: "RedGifs",
  ONLYFANS: "OnlyFans", FANSLY: "Fansly", MANYVIDS: "ManyVids", GMAIL: "Email",
};

function orderedPlatforms(cfg: Record<string, unknown>): string[] {
  const keys = Object.keys(cfg);
  return keys.slice().sort((a, b) => {
    const ia = PREFERRED_ORDER.indexOf(a);
    const ib = PREFERRED_ORDER.indexOf(b);
    if (ia === -1 && ib === -1) return a.localeCompare(b);
    if (ia === -1) return 1;
    if (ib === -1) return -1;
    return ia - ib;
  });
}

interface ConfigPageProps {
  rpc: Rpc;
  electronConnected: boolean;
  // Optional callback fired after a successful save — lets the parent
  // refresh the sidebar's disabled-by-config greying without re-mounting.
  onSaved?: () => void;
}

export function ConfigPage({ rpc, electronConnected, onSaved }: ConfigPageProps) {
  const [cfg, setCfg] = useState<PromoCfg | null>(null);
  const [activeAccountId, setActiveAccountId] = useState<string | null>(null);
  const [activeAccountName, setActiveAccountName] = useState<string>("");
  const [persona, setPersona] = useState<string>("");
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!electronConnected) return;
    let cancelled = false;
    async function load() {
      try {
        // get-account-promo returns the merged view (account override + global
        // defaults), so editing here always shows the account's effective config.
        const accts = await rpc.call<{ accounts: Account[]; activeAccountId: string | null }>("get-accounts");
        if (cancelled) return;
        setActiveAccountId(accts.activeAccountId);
        const active = accts.accounts.find((a) => a.id === accts.activeAccountId);
        setActiveAccountName(active?.displayName || "");
        setPersona(active?.persona || "");
        if (accts.activeAccountId) {
          const promoRes = await rpc.call<{ ok: boolean; data?: PromoCfg; error?: string }>(
            "get-account-promo", accts.activeAccountId,
          );
          if (cancelled) return;
          if (!promoRes.ok) throw new Error(promoRes.error || "load failed");
          setCfg(promoRes.data || {});
        } else {
          // No active account → fall back to global defaults so the page renders something.
          const promoRes = await rpc.call<{ ok: boolean; data?: PromoCfg; error?: string }>("get-promo-config");
          if (cancelled) return;
          if (!promoRes.ok) throw new Error(promoRes.error || "load failed");
          setCfg(promoRes.data || {});
        }
      } catch (e) {
        if (!cancelled) setErr((e as Error).message);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [rpc, electronConnected]);

  function updatePlatform<K extends keyof PlatformPromoCfg>(p: string, key: K, value: PlatformPromoCfg[K]) {
    setCfg((prev) => prev && ({ ...prev, [p]: { ...prev[p], [key]: value } }));
  }

  async function save() {
    if (!cfg) return;
    setSaving(true);
    setErr(null);
    try {
      const tasks: Promise<unknown>[] = [];
      if (activeAccountId) {
        // Promo edits land on the active account, not the global defaults.
        tasks.push(rpc.call("save-account-promo", activeAccountId, cfg));
        tasks.push(rpc.call("update-account", activeAccountId, { persona }));
      } else {
        // No active account — write to globals so something gets persisted.
        tasks.push(rpc.call("save-promo-config", cfg));
      }
      const results = await Promise.all(tasks);
      const promoRes = results[0] as { ok: boolean; error?: string };
      const personaRes = results[1] as { ok: boolean; error?: string } | undefined;
      if (!promoRes.ok) throw new Error(promoRes.error || "save failed");
      if (personaRes && !personaRes.ok) throw new Error(personaRes.error || "update-account failed");
      setSavedAt(Date.now());
      // Tell App.tsx to refresh sidebar greying (the user may have just
      // toggled platforms on/off in this save).
      if (onSaved) onSaved();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setSaving(false);
    }
  }

  if (!electronConnected) {
    return (
      <section className="settings-page">
        <header className="settings-page-header"><h2>📋 Promo Config</h2></header>
        <div className="empty">Electron must be running on the worker PC.</div>
      </section>
    );
  }
  if (!cfg) {
    return (
      <section className="settings-page">
        <header className="settings-page-header"><h2>📋 Promo Config</h2></header>
        <div className="empty">Loading…</div>
      </section>
    );
  }

  return (
    <section className="settings-page">
      <header className="settings-page-header">
        <h2>📋 Promo Config</h2>
        <div className="header-actions">
          {savedAt && Date.now() - savedAt < 4000 && <span className="ok">✓ saved</span>}
          {err && <span className="err">{err}</span>}
          <button className="primary" onClick={save} disabled={saving}>
            {saving ? "Saving…" : "💾 Save"}
          </button>
        </div>
      </header>

      <div className="persona-section">
        <div className="persona-header">
          <h3>🪪 Persona Prompt {activeAccountName && <span className="muted">({activeAccountName})</span>}</h3>
          <p className="hint">
            Free-form description of this creator — voice, body, themes, kinks,
            relationship context, "do not say" list. Injected into every Gemini
            caption call.
          </p>
        </div>
        <textarea
          className="persona-text"
          rows={10}
          placeholder="e.g. Redhead in her late 20s. Voice: playful, lower-case…"
          value={persona}
          onChange={(e) => setPersona(e.target.value)}
        />
      </div>

      <div className="config-grid">
        {orderedPlatforms(cfg).map((p) => {
          const pc = cfg[p];
          return (
            <div key={p} className={`config-card ${pc.paused ? "paused" : ""}`}>
              <header className="config-card-header">
                <span className="config-icon">{PLATFORM_ICON[p] ?? p[0]}</span>
                <h3>{PLATFORM_NAME[p] ?? p}</h3>
                <label
                  className="pause-toggle"
                  title={pc.paused
                    ? "Click to enable scheduled posts for this account/platform"
                    : "Click to disable scheduled posts for this account/platform (Post Now still works)"}
                >
                  <input
                    type="checkbox"
                    checked={!!pc.paused}
                    onChange={(e) => updatePlatform(p, "paused", e.target.checked)}
                  />
                  <span>{pc.paused ? "⏸ Disabled" : "▶ Active"}</span>
                </label>
              </header>

              <label>Post Times <span className="hint">(comma-separated, 24h)</span></label>
              <input
                type="text"
                value={(pc.TIMES || []).join(", ")}
                onChange={(e) => updatePlatform(p, "TIMES",
                  e.target.value.split(",").map((s) => s.trim()).filter(Boolean))}
              />

              <label>Solo %</label>
              <input
                type="number" min={0} max={100}
                value={pc.SOLO_PERCENTAGE ?? 0}
                onChange={(e) => updatePlatform(p, "SOLO_PERCENTAGE", parseInt(e.target.value) || 0)}
              />

              <label>Video % <span className="hint">(0 = all photos, 100 = all videos)</span></label>
              <input
                type="range" min={0} max={100}
                value={pc.VIDEO_PERCENTAGE ?? 50}
                onChange={(e) => updatePlatform(p, "VIDEO_PERCENTAGE", parseInt(e.target.value) || 0)}
              />
              <div className="slider-readout">{100 - (pc.VIDEO_PERCENTAGE ?? 50)}% photo / {pc.VIDEO_PERCENTAGE ?? 50}% video</div>

              <label>Media Types</label>
              <div className="checkboxes">
                {(["image", "video"] as const).map((t) => (
                  <label key={t} className="checkbox">
                    <input
                      type="checkbox"
                      checked={(pc.MEDIA_TYPES || []).includes(t)}
                      onChange={(e) => {
                        const set = new Set(pc.MEDIA_TYPES || []);
                        if (e.target.checked) set.add(t); else set.delete(t);
                        updatePlatform(p, "MEDIA_TYPES", Array.from(set));
                      }}
                    /> {t}
                  </label>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
